import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, Download } from "lucide-react";
import "./LandingPage.scss";
import axios from 'axios'
import heartLoading from "../../../assests/heartLoading.gif"
import ProductViewModal from "./ProductViewModal.jsx";
import MuiDataTable from "./MuiDataTable.jsx";
import { Snackbar, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { isProdEnv } from "../../../env-utils.js";
import exportService from "../../../services/exportService.js";
import { useExport } from "../../../context/ExportContext.jsx";

const ROWS_PER_PAGE = 10;

const LandingPage = () => {

  const [toast, setToast] = React.useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedView, setSelectedView] = useState("products");
  const [products, setProducts] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState(null);
  const [visibleCount, setVisibleCount] = useState(ROWS_PER_PAGE);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const tableScrollRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    getchProductsDetails();
  }, []);

  // Reset visible count when search query changes
  useEffect(() => {
    setVisibleCount(ROWS_PER_PAGE);
  }, [searchQuery]);

  // Infinite scroll observer
  useEffect(() => {
    const rootEl = tableScrollRef.current || null;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && !isFetchingMore) {
          const filtered = products.filter(product =>
            searchQuery ? product.productName.toLowerCase().includes(searchQuery.toLowerCase()) : true
          );
          if (visibleCount < filtered.length) {
            setIsFetchingMore(true);
            setTimeout(() => {
              setVisibleCount(prev => Math.min(prev + ROWS_PER_PAGE, filtered.length));
              setIsFetchingMore(false);
            }, 400);
          }
        }
      },
      {
        root: rootEl,
        threshold: 0,
        rootMargin: "200px 0px",
      }
    );

    const currentSentinel = sentinelRef.current;
    if (currentSentinel) observer.observe(currentSentinel);
    return () => {
      if (currentSentinel) observer.unobserve(currentSentinel);
      observer.disconnect();
    };
  }, [visibleCount, products, searchQuery, isFetchingMore]);

  const getchProductsDetails = async () => {
    try {
      setProductsLoading(true)
      setLoading(true)

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/get-product-submissions`)

      if (response.status === 200 || 201) {
        setProducts(response.data)
      }

    } catch (e) {
      setError("Failed to fetch products. Please try again later.")
    } finally {
      setProductsLoading(false)
      setLoading(false)
    }
  }

  // Filter products by search query
  const currentDataSource = products.filter(product =>
    searchQuery ? product.productName.toLowerCase().includes(searchQuery.toLowerCase()) : true
  );

  const currentRows = currentDataSource.slice(0, visibleCount);
  const hasMore = visibleCount < currentDataSource.length;

  const handleViewProduct = (product) => {
    if (product) {
      setSelectedProductDetails(product);
      setShowViewModal(true);
    }
  };

  const handleDeleteProduct = async (product_id) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/delete-product/${product_id}`)
      if (response?.status === 200) {
        setToast({ open: true, message: 'Product deleted successfully.', severity: 'success' });
        getchProductsDetails();
      }
    } catch (err) {
      setToast({ open: true, message: 'Failed to delete product.', severity: 'error' });
    }
  }

  const role = localStorage.getItem("role") || "";
  const { startExportPolling } = useExport();
  const [isExporting, setIsExporting] = useState(false);

  const handleExportAllProducts = async () => {
    const userId = localStorage.getItem("userId");
    setIsExporting(true);
    try {
      const { job_id } = await exportService.triggerAllSubmissions(userId);
      startExportPolling(job_id, "Products Info", {
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch {
      setIsExporting(false);
    }
  };

  const productColumns = [
    { id: 'productName', label: 'Product Name', sortable: true },
    { id: 'productDescription', label: 'Description', sortable: true },
    { id: 'version', label: 'Version', sortable: true },
    { id: 'status', label: 'Status', sortable: true },
    { id: 'currentPhase', label: 'Current Phase', sortable: true },
    { id: 'elevation_status', label: 'Prod Elevation Status', sortable: true },
    { id: 'versioning', label: 'Versioning', sortable: true },
    { id: 'actions', label: 'Actions', sortable: false }
  ];

  if (loading || productsLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-[9999] w-screen h-screen">
        <div className="flex flex-col items-center gap-3">
          <img src={heartLoading} alt="Loading..." style={{ width : 200}} />
          <span className="text-gray-700 text-[20px] font-medium">
            Hang tight! The resources are loading...
          </span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button onClick={getchProductsDetails} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ paddingLeft: "0px", paddingRight: "0px", backgroundColor: "#eee" }} className="dashboard-container">
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setToast({ ...toast, open: false })} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <div className="dashboard-main">
        <div className="dashboard-header px-4">
          <div className="font-bold font-family-poppins pt-2 text-[24px] text-[#000]">Overview</div>
          <p className="pb-4 text-[#000]">View all essential information about this asset in one place</p>

          {/* Top Actions Bar */}
          <div className="dashboard-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>

              {!isProdEnv() && <>
                {(role === 'Data Steward' || role === "Admin" || role === "Domain Owner" || role === "Product Owner") ? (
                  <Link
                    to="/product-onboarding"
                    className="onboard-button h-[50px] w-[180px] rounded-md !bg-[#6366F1] !text-[#fff] flex items-center justify-center whitespace-nowrap"
                  >
                    <span className="flex items-center gap-1">
                      <AddIcon fontSize="small" /> Onboard Product
                    </span>
                  </Link>
                ) : (
                  <button
                    disabled
                    style={{ color: "#333", cursor: "not-allowed", padding: '4px 6px' }}
                    className="onboard-button bg-gray-100 h-[50px] w-[140px] rounded-md border border-[#818181]"
                  >
                    <span><AddIcon /> New Product</span>
                  </button>
                )}
              </>}

              {/* Search Box */}
              <div className="search-box">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search for Products"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <button
              onClick={handleExportAllProducts}
              disabled={isExporting}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg border transition-colors h-[40px] whitespace-nowrap ${
                isExporting
                  ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                  : "bg-[#bb1ccc] text-white border-[#bb1ccc] "
              }`}
            >
              <Download size={16} />
              {isExporting ? "Exporting..." : "Export Products Info"}
            </button>
          </div>
          <span className="bg-[#dec0dc] text-[#bb1ccc] text-sm px-3 py-1 rounded-md shadow-sm">
           There are  {' '}
            <span>
              {searchQuery ? currentDataSource.length : products.length}
            </span>{' '}
            Unique Result{(searchQuery ? currentDataSource.length : products.length) !== 1 ? 's' : ''} Found
          </span>
        </div>


        <div className="border-b border-gray-300 mb-4"></div>

        {/* Table - only this section scrolls */}
        <div ref={tableScrollRef} className="dashboard-table-section px-4">
          <div className="table-container dashboard-products-table">
            <MuiDataTable
              data={currentRows}
              columns={productColumns}
              onView={handleViewProduct}
              onDelete={handleDeleteProduct}
              onRefresh={getchProductsDetails}
              selectedView={selectedView}
              searchQuery={searchQuery}
              userRole={role}
            />
          </div>

          {/* Infinite scroll sentinel */}
          <div ref={sentinelRef} className="h-[24px]" />


        </div>
      </div>

      {/* View Modal */}
      {showViewModal && (
        <ProductViewModal
          open={showViewModal}
          onClose={() => setShowViewModal(false)}
          product={selectedProductDetails}
        />
      )}
    </div>
  );
};

export default LandingPage;
